import { HomeyAPI } from 'homey-api';
import { v4 as uuid } from 'uuid';

import { RouteManager } from '../../../RouteManager';
import type { CommandItem, CommandProviderArgs } from '../command-provider.types';
import { COMMAND_SECTIONS } from '../command-types.ts';

import { webAppSettingsStore } from '../../../store/web-app-settings/WebAppSettingsStore';

import { glyph } from '../../../theme/glyph';

export function runNewAdvancedFlow({
  api,
  features,
}: Pick<CommandProviderArgs, 'api' | 'features'>): void {
  if (api == null || features == null) {
    return;
  }

  if (features.isCloudPremium === true) {
    RouteManager.toAdvancedFlow(`create?id=${uuid()}`);
    return;
  }

  if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
    RouteManager.toFlows(RouteManager.getStateForPremiumRequiredDialog());
    return;
  }

  if (features.advancedFlow.hasLicense === false) {
    RouteManager.toNewFlow({ viewId: 'purchase' });
    return;
  }

  if (features.advancedFlow.hasVersion === false) {
    RouteManager.toNewFlow({ viewId: 'update' });
    return;
  }

  RouteManager.toAdvancedFlow(`create?id=${uuid()}`);
}

function runNewVariable({
  api,
  selectedHomey,
}: Pick<CommandProviderArgs, 'api' | 'selectedHomey'>): void {
  if (api == null) {
    return;
  }

  const isPremiumLocked = api.tier !== 'premium' && selectedHomey?.platform !== 'local';

  if (isPremiumLocked === true) {
    RouteManager.toFlows(RouteManager.getStateForPremiumRequiredDialog());
    return;
  }

  RouteManager.toNewLogicVariable();
}

export function getStaticCommands(args: CommandProviderArgs): CommandItem[] {
  const { i18n, api, selectedHomey, features, activeContext, webAppSettings } = args;

  if (activeContext != null) {
    return [];
  }

  const isDarkMode =
    webAppSettings?.theme?.darkMode ?? webAppSettingsStore.get()?.theme?.darkMode ?? false;
  const newDeviceTitle = i18n.messageFormatter('header.action.device');
  const newFlowTitle = i18n.messageFormatter('header.action.flow');
  const newAdvancedFlowTitle = i18n.messageFormatter('header.action.advancedFlow');
  const newVariableTitle = i18n.messageFormatter('header.action.logic');
  const newScriptTitle = i18n.messageFormatter('header.action.script');

  const navigationCommands = [
    {
      key: 'cmd:navigation:home',
      kind: 'command',
      section: COMMAND_SECTIONS.NAVIGATION,
      sortOrder: 10,
      title: i18n.messageFormatter('navigation.home'),
      icon: glyph.system.home,
      keywords: ['go home', 'dashboard', 'main'],
      getOpenInNewTabPath() {
        return RouteManager.getPathForHome();
      },
      onRun() {
        RouteManager.toHome();
      },
    },
    {
      key: 'cmd:navigation:devices',
      kind: 'command',
      section: COMMAND_SECTIONS.NAVIGATION,
      sortOrder: 20,
      title: i18n.messageFormatter('navigation.devices'),
      icon: glyph.system.devices,
      keywords: ['go devices', 'device list'],
      meta: {
        contextType: 'scope',
        scopeType: 'devices',
        canEnterContextWithTab: true,
      },
      getOpenInNewTabPath() {
        return RouteManager.getPathForDevices();
      },
      onRun() {
        RouteManager.toDevices();
      },
    },
    {
      key: 'cmd:navigation:flows',
      kind: 'command',
      section: COMMAND_SECTIONS.NAVIGATION,
      sortOrder: 30,
      title: i18n.messageFormatter('navigation.flows'),
      icon: glyph.system.flow,
      keywords: ['go flows', 'automation'],
      meta: {
        contextType: 'scope',
        scopeType: 'flows',
        canEnterContextWithTab: true,
      },
      getOpenInNewTabPath() {
        return RouteManager.getPathForFlows();
      },
      onRun() {
        RouteManager.toFlows();
      },
    },
    {
      key: 'cmd:navigation:insights',
      kind: 'command',
      section: COMMAND_SECTIONS.NAVIGATION,
      sortOrder: 40,
      title: i18n.messageFormatter('navigation.insights'),
      icon: glyph.system.insights,
      keywords: ['go insights', 'charts', 'logs'],
      meta: {
        contextType: 'scope',
        scopeType: 'logs',
        canEnterContextWithTab: true,
      },
      getOpenInNewTabPath() {
        return RouteManager.getPathForInsights();
      },
      onRun() {
        RouteManager.toInsights();
      },
    },
  ];

  if (selectedHomey?.platform === 'local') {
    navigationCommands.push({
      key: 'cmd:navigation:scripts',
      kind: 'command',
      section: COMMAND_SECTIONS.NAVIGATION,
      sortOrder: 50,
      title: i18n.messageFormatter('navigation.script'),
      icon: glyph.system.homeyscript,
      keywords: ['go scripts', 'homeyscript'],
      getOpenInNewTabPath() {
        return RouteManager.getPathForScripts();
      },
      onRun() {
        RouteManager.toScripts();
      },
    });
  }

  navigationCommands.push({
    key: 'cmd:navigation:settings',
    kind: 'command',
    section: COMMAND_SECTIONS.NAVIGATION,
    sortOrder: 60,
    title: i18n.messageFormatter('navigation.settings'),
    icon: glyph.system.settings,
    keywords: ['go settings', 'preferences'],
    meta: {
      contextType: 'scope',
      scopeType: 'settings',
      canEnterContextWithTab: true,
    },
    getOpenInNewTabPath() {
      return RouteManager.getPathForSettings();
    },
    onRun() {
      RouteManager.toSettings();
    },
  });

  const globalActionCommands = [
    {
      key: 'cmd:action:toggle-dark-mode',
      kind: 'command',
      section: COMMAND_SECTIONS.SETTINGS_ACTIONS,
      sortOrder: 10,
      title:
        isDarkMode === true
          ? i18n.messageFormatter('commandPalette.actions.disableDarkMode')
          : i18n.messageFormatter('commandPalette.actions.enableDarkMode'),
      subtitle:
        isDarkMode === true
          ? i18n.messageFormatter('commandPalette.actions.switchToLightAppearance')
          : i18n.messageFormatter('commandPalette.actions.switchToDarkAppearance'),
      icon: isDarkMode === true ? glyph.core.sun : glyph.core.moon,
      keywords: ['toggle dark mode', 'dark mode', 'light mode', 'theme', 'appearance'],
      keepOpenOnShiftEnter: true,
      onRun() {
        webAppSettingsStore.toggleDarkMode();
      },
    },
  ];

  const createCommands = [
    {
      key: 'cmd:create:device',
      kind: 'command',
      section: COMMAND_SECTIONS.CREATE,
      title: newDeviceTitle,
      icon: glyph.system.devices,
      keywords: ['create device', 'new device', 'add device', 'pair device'],
      onRun() {
        RouteManager.toNewDevice();
      },
    },
    {
      key: 'cmd:create:flow',
      kind: 'command',
      section: COMMAND_SECTIONS.CREATE,
      title: newFlowTitle,
      icon: glyph.system.flow,
      keywords: ['create flow', 'new flow'],
      onRun() {
        RouteManager.toFlow(`create?id=${uuid()}`);
      },
    },
    {
      key: 'cmd:create:advanced-flow',
      kind: 'command',
      section: COMMAND_SECTIONS.CREATE,
      title: newAdvancedFlowTitle,
      icon: glyph.system.advancedFlow,
      keywords: ['create advanced flow', 'new advanced flow'],
      onRun() {
        runNewAdvancedFlow({ api, features });
      },
    },
    {
      key: 'cmd:create:variable',
      kind: 'command',
      section: COMMAND_SECTIONS.CREATE,
      title: newVariableTitle,
      icon: glyph.system.logic,
      keywords: ['create variable', 'new variable', 'logic variable'],
      onRun() {
        runNewVariable({ api, selectedHomey });
      },
    },
  ];

  if (selectedHomey?.platform === 'local') {
    createCommands.push({
      key: 'cmd:create:script',
      kind: 'command',
      section: COMMAND_SECTIONS.CREATE,
      title: newScriptTitle,
      icon: glyph.system.homeyscript,
      keywords: ['create script', 'new script', 'homeyscript'],
      onRun() {
        RouteManager.toNewScript();
      },
    });
  }

  return [...navigationCommands, ...globalActionCommands, ...createCommands];
}
